import * as THREE from "three";

export const TOD = ["dawn", "morning", "midday", "afternoon", "sunset", "night"];
export const TOD_KEY = "afae-tod";
const CENTER = { dawn: 6.5, morning: 9.5, midday: 13, afternoon: 16.5, sunset: 19, night: 23 };

// amb: light on the plaster · sun: light through the high openings · ray: shaft colour · el/az: where the sun (or moon) sits
const P = {
  night: { amb: [0.62, 0.62, 0.76], sun: [0.3, 0.35, 0.5], ray: [0.62, 0.72, 1.0], rayK: 0.6, el: 40, az: -0.35, lamp: 1, dapple: 0.3, top: [0.1, 0.13, 0.3], bot: [0.3, 0.36, 0.62] },
  dawn: { amb: [0.86, 0.77, 0.76], sun: [0.45, 0.32, 0.27], ray: [1.0, 0.78, 0.72], rayK: 0.95, el: 13, az: 1.15, lamp: 0.25, dapple: 0.45, top: [0.86, 0.72, 0.84], bot: [1.0, 0.84, 0.66] },
  morning: { amb: [0.9, 0.88, 0.82], sun: [0.4, 0.37, 0.3], ray: [1.0, 0.95, 0.84], rayK: 0.85, el: 30, az: 0.6, lamp: 0, dapple: 0.55, top: [0.7, 0.84, 0.97], bot: [0.98, 0.95, 0.86] },
  midday: { amb: [0.93, 0.92, 0.89], sun: [0.34, 0.33, 0.3], ray: [1.0, 0.99, 0.94], rayK: 0.7, el: 60, az: 0.05, lamp: 0, dapple: 0.5, top: [0.66, 0.82, 0.98], bot: [0.95, 0.97, 0.98] },
  afternoon: { amb: [0.92, 0.85, 0.74], sun: [0.44, 0.34, 0.2], ray: [1.0, 0.85, 0.58], rayK: 0.9, el: 25, az: -0.6, lamp: 0.05, dapple: 0.6, top: [0.74, 0.8, 0.92], bot: [1.0, 0.86, 0.6] },
  sunset: { amb: [0.88, 0.74, 0.7], sun: [0.48, 0.28, 0.22], ray: [1.0, 0.68, 0.56], rayK: 1.0, el: 9, az: -1.15, lamp: 0.55, dapple: 0.4, top: [0.55, 0.45, 0.7], bot: [1.0, 0.66, 0.5] },
};
const KEYS = [[4, "night"], [6.5, "dawn"], [9.5, "morning"], [13, "midday"], [16.5, "afternoon"], [19, "sunset"], [21, "night"], [28, "night"]];
const FIELDS = ["amb", "sun", "ray", "rayK", "el", "az", "lamp", "dapple", "top", "bot"];

export const phaseOf = (h) => (h >= 5 && h < 8 ? "dawn" : h >= 8 && h < 11 ? "morning" : h >= 11 && h < 15 ? "midday" : h >= 15 && h < 18 ? "afternoon" : h >= 18 && h < 20 ? "sunset" : "night");

const URL_TOD = new URLSearchParams(window.location.search).get("tod");
export function todOverride() {
  if (TOD.includes(URL_TOD)) return URL_TOD;
  const s = localStorage.getItem(TOD_KEY);
  return TOD.includes(s) ? s : null;
}

function hourNow() {
  const o = todOverride();
  if (o) return CENTER[o];
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60;
}

const lerp = (a, b, k) => (Array.isArray(a) ? a.map((v, i) => v + (b[i] - v) * k) : a + (b - a) * k);

function presetAt(hour) {
  const h = hour < 4 ? hour + 24 : hour;
  let i = 0;
  while (i < KEYS.length - 2 && h >= KEYS[i + 1][0]) i++;
  const [h0, a] = KEYS[i];
  const [h1, b] = KEYS[i + 1];
  const t = THREE.MathUtils.smoothstep(h, h0, h1);
  return Object.fromEntries(FIELDS.map((f) => [f, lerp(P[a][f], P[b][f], t)]));
}

const v3 = () => ({ value: new THREE.Vector3() });
export const day = {
  uAmb: v3(), uSunCol: v3(), uSunDir: v3(), uRayCol: v3(), uSkyTop: v3(), uSkyBot: v3(),
  uRayK: { value: 1 }, uLamp: { value: 0 }, uDapple: { value: 0.5 }, uMotion: { value: 1 },
};
const st = { cur: null, target: null, hour: 0, check: 0 };

function apply(p) {
  day.uAmb.value.fromArray(p.amb);
  day.uSunCol.value.fromArray(p.sun);
  day.uRayCol.value.fromArray(p.ray);
  day.uSkyTop.value.fromArray(p.top);
  day.uSkyBot.value.fromArray(p.bot);
  day.uRayK.value = p.rayK;
  day.uLamp.value = p.lamp;
  day.uDapple.value = p.dapple;
  const el = THREE.MathUtils.degToRad(p.el);
  day.uSunDir.value.set(Math.sin(p.az) * Math.cos(el), Math.sin(el), -Math.cos(p.az) * Math.cos(el)).normalize();
}

// Light eases toward the local hour (~40s time constant), so crossing a boundary drifts over minutes, never jumps.
export function updateDay(dt, still) {
  day.uMotion.value = still ? 0 : 1;
  st.check -= dt;
  if (!st.cur || st.check <= 0) {
    st.check = 5;
    st.hour = hourNow();
    st.target = presetAt(st.hour);
    if (!st.cur) st.cur = st.target;
  }
  const k = 1 - Math.exp(-Math.min(dt, 0.5) / 40);
  st.cur = Object.fromEntries(FIELDS.map((f) => [f, lerp(st.cur[f], st.target[f], k)]));
  apply(st.cur);
  window.__afaeDaylight = { phase: phaseOf(st.hour), hour: +st.hour.toFixed(2), override: todOverride(), still: !!still, lamp: +st.cur.lamp.toFixed(2), sunEl: +st.cur.el.toFixed(1) };
}
